<template>
  <div class="flex flex-col gap-[15px]">
    <div class="flex-grow flex flex-col overflow-hidden gap-[10px]">
      <div class="flex flex-col justify-between">
        <span class="text-[19px] font-medium">{{ title }}</span>
        <span class="text-[12px] font-medium text-[#b8b8b8] px-[6px]">
          {{ displayTime }}
        </span>
      </div>
      <div class="overflow-y-auto flex-grow custom-scroll inspiration" ref="inspirationRef">
        <MarkdownPreview :remarkPlugins="[remarkGfm]" class="result flex flex-col flex-1 h-full w-full"
          :source="currentResult.content" />
      </div>
    </div>

    <div :class="[
          'flex flex-col gap-[10px]',
          {
            disabled: currentResult.generateStatus === '生成中'
          }
        ]">
      <div class="flex w-full justify-between items-center pl-[15px]">
        <div class="flex gap-[20px]">
          <div class="flex gap-[2px] items-center cursor-pointer duration-300 opacity-60 hover:opacity-100"
            @click="copy">
            <el-icon class="cursor-pointer"><i-ep-documentCopy /></el-icon>
            <span class="text-[12px]"> 复制 </span>
          </div>

          <el-popconfirm width="220" confirm-button-text="确认" cancel-button-text="取消" @confirm="generateScript(taskKey)"
            icon-color="#626AEF" title="确定重新生成？已有剧本将被覆盖。">
            <template #reference>
              <div class="flex gap-[2px] items-center cursor-pointer duration-300 opacity-60 hover:opacity-100">
                <el-icon class="cursor-pointer" color="red"><i-ep-refresh /></el-icon>
                <span class="text-[12px]">重新生成剧本 </span>
              </div>
            </template>
          </el-popconfirm>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, reactive, ref, watch } from 'vue'
import dayjs from 'dayjs'

import MarkdownPreview from '@uivjs/vue-markdown-preview'
import { copyToClipboard } from '../../utils/utils'
import { ElMessage } from 'element-plus'
import '@uivjs/vue-markdown-preview/markdown.css'
import remarkGfm from 'remark-gfm'
import { useTaskStore } from '../../store/taskStore'
import { useLLMStore } from '../../store/llm'
const props = defineProps({
  taskDetail: {
    type: Object,
    required: true,
    validator: (value) => {
      if (!value) {
        console.warn('taskDetail is required')
        return false
      }

      return true
    }
  }
})

const emits = defineEmits(['watchScriptGenerateStatus'])

const taskStore = useTaskStore()
const llmStore = useLLMStore()

const inspirationRef = ref(null)
const taskId = ref(props.taskDetail.taskId)
const taskKey = ref(props.taskDetail.taskKey)

const results = computed(() => llmStore.results[taskId.value])

const displayTime = computed(() => {
  if (!results.value?.[scriptKey.value]?.create_time) return ''
  return dayjs(results.value?.[scriptKey.value].create_time).format('YYYY-MM-DD HH:mm:ss')
})

const title = computed(() => {
  const [, key] = taskKey.value.split('_')
  return `${taskStore.currentTaskDetail.content.product_name}剧本-${key}`
})

const scriptKey = computed(() => {
  const [, key] = taskKey.value.split('_')
  return `script_${key}`
})

const currentResult = computed(() => {
  return results.value?.[scriptKey.value] ?? {}
})

const currentIndustryId =  computed(() => {
  return taskStore.currentTaskDetail.industry_id
})

const scriptParams = reactive({
  variables: {
    content: results.value[props.taskDetail.inspKey].content
  },
  keys: [scriptKey.value],
  llm_type: 'inspiration_to_script_table',
  clear_keys: [scriptKey.value],
  task_id: taskId.value,
  industry_id: currentIndustryId.value,
})

const clearAboutGenerate = (key) => {
  const currentResult = llmStore.results[taskId.value][key]
  currentResult.content = ''
  currentResult.create_time = ''
}

const generateScript = () => {
  // 生成剧本
  clearAboutGenerate(scriptKey.value)
  taskStore.generateTask(scriptParams)
}

const copy = async () => {
  const content = currentResult.value.content
  if (!content) {
    ElMessage.warning('没有可复制的内容')
    return
  }

  try {
    await copyToClipboard(content, 'text/plain')
    ElMessage.success('内容已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    ElMessage.error('复制失败')
  }

  // 如果需要，这里可以添加事件跟踪逻辑
  // const eventTrackingParmas = {
  //   customized_id: props.taskDetail.id + '',
  //   event_name: 'ScriptCopy',
  //   event_type: 'Click',
  //   event_time: 0,
  //   page_name: '',
  // }
  // eventTrackingFunc(eventTrackingParmas)
}

watch(
  () => currentResult.value.generateStatus,
  (generateStatus) => {
    emits('watchScriptGenerateStatus', { key: scriptKey.value, status: generateStatus })
  },
  { immediate: true }
)
</script>
<style lang="scss" scoped>
.custom-scroll {
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f5f4ff;
    border-radius: 3px;
  }
}

.markdown-body {
  margin: 0;
  font-size: 14px;
  border-radius: 8px;
  background: rgba(106, 80, 235, 0.03);
  box-shadow: inset 0 0 3px 1px rgba(106, 80, 235, 0.1);
  padding: 15px;
  width: 100%;
  max-width: unset;

  hr {
    display: none;
  }
}

:deep(.markdown-body hr) {
  display: none !important;
}

:deep(.markdown-body *) {
  margin: 5px 0;
}

:deep(.markdown-body ul) {
  padding-left: 10px;
}

:deep(.markdown-body li) {
  margin: 5px 0;
}

:deep(.markdown-body a.anchor) {
  display: none;
}

:deep(.markdown-body h1) {
  font-size: 16px;
  line-height: normal;
  font-weight: normal;
}

:deep(.markdown-body h2) {
  font-size: 15px;
  line-height: normal;
  padding-bottom: 10px;
  font-weight: normal;
  border: none;
}

:deep(.markdown-body h3) {
  font-size: 14px;
  line-height: normal;
  font-weight: normal;
}

:deep(.markdown-body h4) {
  font-size: 13px;
  line-height: normal;
  font-weight: normal;
}

:deep(.markdown-body h5) {
  font-size: 12px;
  line-height: normal;
  font-weight: normal;
}

:deep(.el-textarea) {
  height: 100%;

  .el-textarea__inner {
    height: 100%;
  }
}

:deep(.el-textarea__inner) {
  font-size: 14px;
  background: #f7f7f7;
  box-shadow: none;
}

.tooltip-class {
  background: red !important;
}

.disabled {
  opacity: 0.35;
  pointer-events: none;
  cursor: not-allowed;
  user-select: none;
}

.error {
  box-shadow: inset 0 0 1px 1px red;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
